import React, { useState, useRef, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, Bot, MapPin } from 'lucide-react';

// Widget visitor shown on a conversation-history row: country flag + code,
// IP, browser / OS, device. Click for the full details. `visitor` comes from
// the `visitor` field on /sessions and /sessions/call-history rows; phone and
// API sessions have none and render a dash.

export const countryFlag = (code) => {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)));
};

const DeviceIcon = ({ browser, className }) => {
  if (browser?.is_bot) return <Bot className={className} />;
  if (browser?.device_type === 'mobile') return <Smartphone className={className} />;
  if (browser?.device_type === 'tablet') return <Tablet className={className} />;
  return <Monitor className={className} />;
};

const browserLabel = (b) => {
  if (!b) return null;
  const name = b.browser ? `${b.browser}${b.browser_version ? ` ${b.browser_version.split('.')[0]}` : ''}` : null;
  const os = b.os ? `${b.os}${b.os_version ? ` ${b.os_version}` : ''}` : null;
  return [name, os].filter(Boolean).join(' · ') || null;
};

const place = (v) => [v.city, v.region, v.country || v.country_code].filter(Boolean).join(', ');

const fmt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return isNaN(d) ? iso : d.toLocaleString();
};

// Plain-text form for CSV exports.
export const visitorExportText = (v) => {
  if (!v) return '-';
  return [
    v.ip_address,
    place(v) || null,
    browserLabel(v.browser),
    v.browser?.device_type,
  ].filter(Boolean).join(' | ') || '-';
};

const Row = ({ label, value, mono }) =>
  value == null || value === '' ? null : (
    <div className="flex gap-3 py-1">
      <span className="w-28 flex-shrink-0 text-muted-foreground">{label}</span>
      <span className={`text-foreground break-all ${mono ? 'font-mono' : ''}`}>{String(value)}</span>
    </div>
  );

const VisitorBadge = ({ visitor }) => {
  const [pos, setPos] = useState(null); // fixed-position popover; history tables scroll, so absolute would clip
  const ref = useRef(null);
  const open = pos !== null;

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setPos(null); };
    // Scrolling the popover itself must not close it.
    const dismiss = (e) => { if (e?.target && ref.current?.contains(e.target)) return; setPos(null); };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);

  const toggle = (e) => {
    if (open) { setPos(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const width = Math.min(380, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const below = window.innerHeight - r.bottom > 360;
    setPos(below ? { left, top: r.bottom + 8, width } : { left, bottom: window.innerHeight - r.top + 8, width });
  };

  if (!visitor) return <span className="text-sm text-muted-foreground">-</span>;

  const b = visitor.browser || {};
  const s = visitor.session || {};
  const location = place(visitor);

  return (
    <div ref={ref}>
      <button
        type="button"
        onClick={toggle}
        className="flex flex-col items-start gap-0.5 text-left min-w-[150px] hover:opacity-80"
        title="Show visitor details"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground whitespace-nowrap">
          <span className="text-base leading-none">{countryFlag(visitor.country_code) || <MapPin className="w-3.5 h-3.5 text-muted-foreground" />}</span>
          {visitor.country_code || 'Unknown'}
          {visitor.city && <span className="text-muted-foreground font-normal truncate max-w-[110px]">{visitor.city}</span>}
        </span>
        <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">{visitor.ip_address || '-'}</span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
          <DeviceIcon browser={b} className="w-3 h-3 flex-shrink-0" />
          {browserLabel(b) || 'Unknown browser'}
        </span>
      </button>

      {open && (
        <div style={{ position: 'fixed', ...pos }} className="z-50 max-h-[70vh] overflow-y-auto bg-card border border-border rounded-lg shadow-xl p-4 text-xs">
          <div className="text-sm font-semibold text-foreground mb-2">Visitor details</div>
          <Row label="IP address" value={visitor.ip_address} mono />
          <Row label="Location" value={location} />
          <Row label="Timezone" value={visitor.timezone} />
          <Row label="Network" value={visitor.geo?.org} />
          <Row label="Browser" value={b.browser ? `${b.browser} ${b.browser_version || ''}`.trim() : null} />
          <Row label="OS" value={b.os ? `${b.os} ${b.os_version || ''}`.trim() : null} />
          <Row label="Device" value={b.device_type ? `${b.device_type}${b.is_bot ? ' (bot)' : ''}` : null} />
          <Row label="Language" value={visitor.accept_language || visitor.language} />
          <Row label="Page" value={visitor.referrer} />
          <Row label="Origin" value={visitor.origin} />
          <Row label="Do Not Track" value={visitor.do_not_track ? 'Yes' : null} />
          <Row label="Entry point" value={s.entry_endpoint} mono />
          <Row label="Session ID" value={s.session_id} mono />
          <Row label="Started" value={fmt(s.started_at)} />
          <Row label="Ended" value={fmt(s.ended_at)} />
          <Row label="User agent" value={visitor.user_agent} mono />
        </div>
      )}
    </div>
  );
};

// One-line, non-interactive form for compact lists (Session Analysis cards).
export const VisitorInline = ({ visitor }) => {
  if (!visitor) return null;
  const b = visitor.browser || {};
  return (
    <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground min-w-0" title={visitorExportText(visitor)}>
      <span className="leading-none">{countryFlag(visitor.country_code)}</span>
      <span className="font-medium text-foreground">{visitor.country_code || '??'}</span>
      <span className="font-mono truncate">{visitor.ip_address}</span>
      <DeviceIcon browser={b} className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{browserLabel(b)}</span>
    </div>
  );
};

export default VisitorBadge;
